import { chatSuggestionsService, type PersonalizedSuggestion, type ChatGreeting } from "./chatSuggestionsService";
import { quotesService, type Quote } from "./quotesService";

interface CachedContent<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface PrefetchedChatContent {
  suggestions: PersonalizedSuggestion[];
  greeting: ChatGreeting;
}

interface PrefetchedBrainContent {
  quotes: Quote[];
}

interface ContentCache {
  chat: CachedContent<PrefetchedChatContent> | null;
  brain: CachedContent<PrefetchedBrainContent> | null;
}

type ContentType = "chat" | "brain" | "all";

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const BACKGROUND_REFRESH_DELAY = 2000; // 2 seconds after action

class ContentPrefetchService {
  private cache: ContentCache = {
    chat: null,
    brain: null,
  };
  private fetchInProgress: Map<string, Promise<any>> = new Map();
  private listeners: Map<string, Set<(data: any) => void>> = new Map();
  private currentUserId: string | null = null;
  private currentUserName: string | undefined = undefined;

  setUser(userId: string, userName?: string) {
    if (this.currentUserId !== userId) {
      this.currentUserId = userId;
      this.currentUserName = userName;
      this.cache = { chat: null, brain: null };
    } else {
      this.currentUserName = userName;
    }
  }

  private isCacheValid<T>(cached: CachedContent<T> | null): boolean {
    if (!cached) return false;
    return Date.now() < cached.expiresAt;
  }

  private setCacheEntry<T>(key: keyof ContentCache, data: T): CachedContent<T> {
    const entry: CachedContent<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + CACHE_TTL,
    };
    (this.cache[key] as any) = entry;
    return entry;
  }

  getCachedChatContent(): PrefetchedChatContent | null {
    if (this.isCacheValid(this.cache.chat)) {
      return this.cache.chat!.data;
    }
    return null;
  }

  getCachedBrainContent(): PrefetchedBrainContent | null {
    if (this.isCacheValid(this.cache.brain)) {
      return this.cache.brain!.data;
    }
    return null;
  }

  getDefaultChatContent(): PrefetchedChatContent {
    return {
      suggestions: chatSuggestionsService.getDefaultSuggestions(),
      greeting: chatSuggestionsService.getDefaultGreeting(this.currentUserName),
    };
  }

  getDefaultBrainContent(): PrefetchedBrainContent {
    return {
      quotes: quotesService.getDefaultQuotes().slice(0, 5),
    };
  }

  async prefetchChatContent(forceRefresh = false): Promise<PrefetchedChatContent> {
    if (!this.currentUserId) {
      return this.getDefaultChatContent();
    }

    if (!forceRefresh && this.isCacheValid(this.cache.chat)) {
      return this.cache.chat!.data;
    }

    const cacheKey = `chat-${this.currentUserId}`;
    if (this.fetchInProgress.has(cacheKey)) {
      return this.fetchInProgress.get(cacheKey)!;
    }

    const fetchPromise = (async () => {
      try {
        const [suggestions, greeting] = await Promise.all([
          chatSuggestionsService.getPersonalizedSuggestions(this.currentUserId!),
          chatSuggestionsService.getPersonalizedGreeting(this.currentUserId!, this.currentUserName),
        ]);

        const content: PrefetchedChatContent = { suggestions, greeting };
        this.setCacheEntry("chat", content);
        this.notifyListeners("chat", content);
        return content;
      } catch (error) {
        console.error("Failed to prefetch chat content:", error);
        return this.getDefaultChatContent();
      } finally {
        this.fetchInProgress.delete(cacheKey);
      }
    })();

    this.fetchInProgress.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  async prefetchBrainContent(forceRefresh = false): Promise<PrefetchedBrainContent> {
    if (!this.currentUserId) {
      return this.getDefaultBrainContent();
    }

    if (!forceRefresh && this.isCacheValid(this.cache.brain)) {
      return this.cache.brain!.data;
    }

    const cacheKey = `brain-${this.currentUserId}`;
    if (this.fetchInProgress.has(cacheKey)) {
      return this.fetchInProgress.get(cacheKey)!;
    }

    const fetchPromise = (async () => {
      try {
        const quotes = await quotesService.getPersonalizedQuotes(this.currentUserId!, 5);
        const content: PrefetchedBrainContent = { quotes };
        this.setCacheEntry("brain", content);
        this.notifyListeners("brain", content);
        return content;
      } catch (error) {
        console.error("Failed to prefetch brain content:", error);
        return this.getDefaultBrainContent();
      } finally {
        this.fetchInProgress.delete(cacheKey);
      }
    })();

    this.fetchInProgress.set(cacheKey, fetchPromise);
    return fetchPromise;
  }

  async prefetchAll(forceRefresh = false): Promise<void> {
    await Promise.all([
      this.prefetchChatContent(forceRefresh),
      this.prefetchBrainContent(forceRefresh),
    ]);
  }

  scheduleBackgroundRefresh(type: ContentType = "all") {
    setTimeout(() => {
      if (type === "all" || type === "chat") {
        this.prefetchChatContent(true);
      }
      if (type === "all" || type === "brain") {
        this.prefetchBrainContent(true);
      }
    }, BACKGROUND_REFRESH_DELAY);
  }

  onContentUpdate(type: "chat" | "brain", callback: (data: any) => void): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(callback);

    return () => {
      this.listeners.get(type)?.delete(callback);
    };
  }

  private notifyListeners(type: "chat" | "brain", data: any) {
    this.listeners.get(type)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error("Error in content update listener:", error);
      }
    });
  }

  invalidateCache(type?: ContentType) {
    if (!type || type === "all") {
      this.cache = { chat: null, brain: null };
    } else if (type === "chat") {
      this.cache.chat = null;
    } else if (type === "brain") {
      this.cache.brain = null;
    }
  }

  onThreadComplete() {
    this.scheduleBackgroundRefresh("chat");
  }

  onBrainWorkoutComplete() {
    this.scheduleBackgroundRefresh("brain");
  }
}

export const contentPrefetchService = new ContentPrefetchService();
